import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown, Search, X, ShieldCheck, Landmark, TrendingUp,
  Banknote, Home, BookOpen, Mail,
  Car, HeartHandshake, Plane, Activity, Flame, Building2, BarChart3,
  CheckCircle, Briefcase, Settings,
  Wallet, Smartphone, GraduationCap, PiggyBank, LineChart, Layers,
  PieChart, Calendar, Coins, ShieldAlert, Ship, Scale, QrCode,
  CreditCard, Award, Rocket, Wrench, FileText, Package, RefreshCw,
  MapPin, AlertTriangle, Sparkles, HeartPulse, ShoppingCart, Globe,
  ArrowLeftRight, Users, SlidersHorizontal, ChevronLeft,
  Check, UtensilsCrossed, Gamepad2,
} from "lucide-react";
import Footer from "../../components/footer/Footer";
import {
  FAQ_INDUSTRIES,
  ALL_FAQS_FLAT,
  getFaqItemsByIndustry,
  ALL_FAQS_BY_ITEM_KEY,
  TOTAL_INFOBOXX_FAQS,
} from "../../data/faqsData";

/* Brand palette — FAQs experience */
const THEME_GOLD = "#FFD221";
const THEME_INK = "#000000";

/** Light shell — matches Contact / Partners cream + gold */
const THEME_CREAM = "#f7f4ec";
const gradLightHero = `linear-gradient(180deg, #ffffff 0%, ${THEME_CREAM} 52%, #ffffff 100%)`;
const gradLightHeader = `linear-gradient(135deg, #ffffff 0%, #fffdf6 45%, ${THEME_CREAM} 100%)`;
const gradLightCta = `linear-gradient(135deg, #fffdf6 0%, ${THEME_CREAM} 50%, #ffffff 100%)`;

const shadowGoldTile = "0 10px 28px -12px rgba(255, 210, 33, 0.42)";
const shadowGoldTileSm = "0 8px 22px -10px rgba(255, 210, 33, 0.35)";
const shadowGoldSm = "0 8px 24px -8px rgba(0, 0, 0, 0.1)";
const shadowGold = "0 12px 32px -10px rgba(0, 0, 0, 0.12)";
const shadowGoldFab = "0 16px 40px -12px rgba(255, 210, 33, 0.5)";

/* ─── Icon map ─────────────────────────────────── */
const ICON_MAP = {
  ShieldCheck, Landmark, TrendingUp, Banknote, Home, BookOpen,
  Car, HeartHandshake, Plane, Activity, Flame, Building2, BarChart3,
  CheckCircle, Briefcase, Settings, Mail,
  Wallet, Smartphone, GraduationCap, PiggyBank, LineChart, Layers,
  PieChart, Calendar, Coins, ShieldAlert, Ship, Scale, QrCode,
  CreditCard, Award, Rocket, Wrench, FileText, Package, RefreshCw,
  MapPin, AlertTriangle, Sparkles, HeartPulse, ShoppingCart, Globe,
  ArrowLeftRight, Users, UtensilsCrossed, Gamepad2,
};
const I = (name) => ICON_MAP[name] ?? BookOpen;

/* ─── Data mapping helpers ─────────────────────── */
const SEARCHABLE_FAQS_FLAT = ALL_FAQS_FLAT.map((faq) => ({ ...faq, generated: false }));
const TOTAL_FAQS = TOTAL_INFOBOXX_FAQS;
const DEFAULT_TOPIC_ID = FAQ_INDUSTRIES[0]?.id ?? "real-estate-mortgages";
const FEATURED_HELP_CARDS = [
  {
    title: "Start with a topic",
    text: "Choose the area closest to your question and browse focused answers.",
    icon: Layers,
  },
  {
    title: "Search across InfoBoxx",
    text: "Use keywords like land title, NHIS, CV, meal plan, or betting limits.",
    icon: Search,
  },
  {
    title: "Still unsure?",
    text: "Contact the team and we will point you to the right expert journey.",
    icon: Mail,
  },
];

/* ─── Animation variants ────────────────────────── */
const drawerVariants = {
  hidden: { x: "100%", opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { type: "spring", damping: 28, stiffness: 260 } },
  exit:    { x: "100%", opacity: 0, transition: { duration: 0.22, ease: "easeIn" } },
};
const backdropVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.22 } },
  exit:    { opacity: 0, transition: { duration: 0.2 } },
};
const fadeUp = {
  hidden:  { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: i * 0.045 },
  }),
};
const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

/* ─── Single FAQ accordion item ─────────────────── */

/** Doughnut: page read progress + lively spinner while scrolling (desktop sidebar) */
const SidebarScrollDoughnut = ({ scrollPct, isScrolling }) => {
  const r = 38;
  const c = 2 * Math.PI * r;
  const dash = (Math.min(100, Math.max(0, scrollPct)) / 100) * c;
  return (
    <div className="relative mx-auto aspect-square w-[5.25rem] shrink-0">
      {isScrolling && (
        <div
          className="pointer-events-none absolute inset-[-4px] rounded-full border-2 border-black/[0.06] border-t-[#FFD221] border-r-[#FFD221]/45 opacity-95 animate-spin"
          style={{ animationDuration: "0.75s" }}
          aria-hidden
        />
      )}
      <svg viewBox="0 0 100 100" className="relative z-[1] h-full w-full -rotate-90" aria-hidden>
        <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(0,0,0,0.07)" strokeWidth="9" />
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke={THEME_GOLD}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          className="transition-[stroke-dasharray] duration-200 ease-out"
        />
      </svg>
      <div className="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center gap-px">
        <span className="text-[14px] font-black tabular-nums leading-none text-[#000000]">{scrollPct}</span>
        <span className="translate-y-0.5 text-[9px] font-bold leading-none text-neutral-400">%</span>
      </div>
    </div>
  );
};

const FaqItem = ({ faq, index, color, isOpen, onToggle }) => (
  <motion.div layout className="border-b border-black/[0.06] last:border-0" initial={false}>
    <button
      type="button"
      onClick={onToggle}
      className="flex gap-4 justify-between items-start px-5 py-4 w-full text-left group"
    >
      <span className="flex gap-3 items-start min-w-0">
        <span
          className="shrink-0 mt-0.5 w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center shadow-sm"
          style={{ backgroundColor: `${THEME_GOLD}33`, color: THEME_INK }}
        >
          {index + 1}
        </span>
        <span className={`text-sm font-semibold leading-snug transition-colors ${isOpen ? "text-[#000000]" : "text-neutral-700"} group-hover:text-[#000000]`}>
          {faq.q}
        </span>
      </span>
      <motion.span
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.25 }}
        className="shrink-0 mt-0.5"
        style={{ color: isOpen ? color : "rgba(0,0,0,0.35)" }}
      >
        <ChevronDown size={18} strokeWidth={2.5} />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="answer"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="overflow-hidden"
        >
          <div className="px-5 pb-5 pl-14">
            {faq.generated && (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 mb-3 rounded-full text-[10px] font-bold uppercase tracking-[0.16em]"
                style={{ backgroundColor: `${THEME_GOLD}22`, color: THEME_INK }}
              >
                Suggested answer
              </span>
            )}
            <p className="text-sm leading-relaxed text-neutral-600">{faq.a}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

/* ─── Category block ────────────────────────────── */
const CategoryBlock = ({ cat, color, openId, setOpenId }) => {
  const CatIcon = I(cat.icon);
  return (
    <motion.div
      id={`cat-${cat.id}`}
      variants={fadeUp}
      className="group/card relative rounded-2xl border border-black/[0.07] bg-white overflow-hidden shadow-[0_8px_34px_-12px_rgba(0,0,0,0.12)] hover:shadow-[0_16px_40px_-16px_rgba(0,0,0,0.18)] transition-shadow duration-300"
    >
      <div
        className="absolute top-0 left-0 right-0 h-[3px] opacity-90 pointer-events-none"
        style={{ background: `linear-gradient(90deg, ${THEME_GOLD} 0%, ${THEME_GOLD}55 45%, transparent 100%)` }}
      />
      <div
        className="flex items-center gap-3 px-5 py-4 border-b border-black/[0.06]"
        style={{ background: `linear-gradient(135deg, ${THEME_GOLD}12 0%, transparent 72%)` }}
      >
        <div
          className="flex justify-center items-center w-9 h-9 rounded-xl ring-1 shadow-sm ring-black/5"
          style={{ background: `${THEME_GOLD}28`, color: THEME_INK }}
        >
          <CatIcon size={17} strokeWidth={2} />
        </div>
        <h3 className="font-bold text-[#000000] text-sm flex-1 tracking-tight">{cat.label}</h3>
        {cat.faqs.length > 0 && (
          <span
            className="text-[10px] font-bold px-2 py-1 rounded-lg text-[#000000]"
            style={{ background: `${THEME_GOLD}35` }}
          >
            {cat.faqs.length} FAQs
          </span>
        )}
      </div>
      <div>
        {cat.faqs.length === 0 ? (
          <div className="flex gap-3 items-center px-5 py-7 text-sm text-neutral-400">
            <BookOpen size={16} className="shrink-0 text-[#000000]/35" />
            No FAQs available yet for this category.
          </div>
        ) : (
          cat.faqs.map((faq, i) => (
            <FaqItem
              key={i}
              faq={faq}
              index={i}
              color={color}
              isOpen={openId === `${cat.id}-${i}`}
              onToggle={() => setOpenId(openId === `${cat.id}-${i}` ? null : `${cat.id}-${i}`)}
            />
          ))
        )}
      </div>
    </motion.div>
  );
};

/* ─── Search results ───────────────────────────── */
const SearchResults = ({ results, openId, setOpenId }) => (
  <div className="space-y-2">
    {results.length === 0 ? (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col justify-center items-center py-24 text-center"
      >
        <div
          className="flex justify-center items-center mb-5 w-20 h-20 rounded-3xl ring-1 ring-black/5"
          style={{ background: `${THEME_GOLD}18` }}
        >
          <Search size={32} style={{ color: `${THEME_GOLD}` }} className="opacity-80" />
        </div>
        <p className="font-bold text-[#000000] text-lg mb-1">No results found</p>
        <p className="max-w-xs text-sm text-neutral-500">Try different keywords or pick another topic with the filter.</p>
      </motion.div>
    ) : (
      results.map((faq, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: i * 0.03 }}
          className="rounded-2xl border border-black/[0.07] bg-white overflow-hidden shadow-[0_6px_28px_-10px_rgba(0,0,0,0.1)]"
        >
          <div
            className="flex items-center gap-2 px-4 py-2.5 text-[11px] font-bold border-b border-black/[0.06] text-[#000000]"
            style={{ background: `linear-gradient(90deg, ${THEME_GOLD}22 0%, ${THEME_GOLD}08 100%)` }}
          >
            {faq.industryLabel}
            <span className="opacity-35 mx-0.5 text-[#000000]">·</span>
            {faq.categoryLabel}
          </div>
          <FaqItem
            faq={faq}
            index={i}
            color={THEME_GOLD}
            isOpen={openId === `search-${i}`}
            onToggle={() => setOpenId(openId === `search-${i}` ? null : `search-${i}`)}
          />
        </motion.div>
      ))
    )}
  </div>
);

/* ─── Right-side Filter Drawer ────────────────── */
const FilterDrawer = ({
  isOpen,
  onClose,
  activeIndustryId,
  activeCatId,
  onIndustryChange,
  onCatChange,
}) => {
  const [drawerStep, setDrawerStep] = useState("topics");
  const bodyRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDrawerStep("topics");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const activeIndustry = FAQ_INDUSTRIES.find((i) => i.id === activeIndustryId) ?? FAQ_INDUSTRIES[0];
  const activeIndustryItems = getFaqItemsByIndustry(activeIndustryId);

  const topicFaqCount = (topicId) => {
    const items = getFaqItemsByIndustry(topicId);
    return items.reduce((acc, item) => {
      const key = `${topicId}::${String(item.id)}`;
      return acc + (ALL_FAQS_BY_ITEM_KEY.get(key)?.length ?? 0);
    }, 0);
  };

  const sectionFaqCount = (itemId) => {
    const key = `${activeIndustryId}::${String(itemId)}`;
    return ALL_FAQS_BY_ITEM_KEY.get(key)?.length ?? 0;
  };

  const sectionCountForTopic = (topicId) => getFaqItemsByIndustry(topicId).length;

  const handleTopicPick = (topicId) => {
    onIndustryChange(topicId);
    const n = sectionCountForTopic(topicId);
    if (n <= 1) {
      onCatChange(null);
      onClose();
    } else {
      setDrawerStep("sections");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-[105] backdrop-blur-[2px]"
            style={{ background: "rgba(0, 0, 0, 0.45)" }}
            onClick={onClose}
          />

          {/* Drawer panel */}
          <motion.div
            key="drawer"
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed top-0 right-0 bottom-0 z-[110] w-full sm:w-[420px] flex flex-col bg-white shadow-2xl"
          >
            {/* Drawer header */}
            <div
              className="overflow-hidden relative px-6 pt-6 pb-5 shrink-0"
              style={{ background: gradLightHeader }}
            >
              <div
                className="absolute inset-0 opacity-[0.12]"
                style={{
                  background: `radial-gradient(ellipse 90% 70% at 20% 0%, ${THEME_GOLD}35 0%, transparent 55%)`,
                }}
              />
              <div
                className="absolute inset-0 opacity-[0.07]"
                style={{
                  backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)",
                  backgroundSize: "22px 22px",
                }}
              />
              <div className="flex relative z-10 justify-between items-start gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] mb-1.5" style={{ color: THEME_GOLD }}>
                    InfoBoxx FAQs
                  </p>
                  <h2 className="text-xl font-black leading-tight text-gray-950">
                    {drawerStep === "topics" ? "Pick a topic" : activeIndustry.label}
                  </h2>
                  <p className="mt-1 text-xs leading-relaxed text-gray-500">
                    {drawerStep === "topics"
                      ? `${TOTAL_INFOBOXX_FAQS} answers across ${FAQ_INDUSTRIES.length} topics — real estate, legal, nutrition, health, careers, betting & gaming.`
                      : activeIndustryItems.length > 1
                        ? `This topic has ${activeIndustryItems.length} sections. Choose one or view all questions.`
                        : `${topicFaqCount(activeIndustryId)} questions in this topic.`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition-all hover:bg-black/5 hover:text-gray-900"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Step chips — section step only matters when a topic has multiple FAQ groups */}
              <div className="flex relative z-10 flex-wrap gap-2 items-center mt-5">
                <button
                  type="button"
                  onClick={() => setDrawerStep("topics")}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold transition-all ${
                    drawerStep === "topics"
                      ? ""
                      : "bg-black/[0.06] text-gray-600 hover:bg-black/[0.08]"
                  }`}
                  style={
                    drawerStep === "topics"
                      ? { background: THEME_GOLD, color: THEME_INK }
                      : undefined
                  }
                >
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black"
                    style={{
                      background: drawerStep === "topics" ? "rgba(31, 28, 25, 0.4)" : "rgba(0,0,0,0.08)",
                      color: drawerStep === "topics" ? THEME_INK : "inherit",
                    }}
                  >
                    1
                  </span>
                  Topic
                </button>
                <div className="hidden h-px w-5 bg-black/10 sm:block" />
                <button
                  type="button"
                  onClick={() => setDrawerStep("sections")}
                  disabled={!activeIndustryId || activeIndustryItems.length <= 1}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold transition-all ${
                    drawerStep === "sections"
                      ? ""
                      : "bg-black/[0.06] text-gray-600 hover:bg-black/[0.08] disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-black/[0.06]"
                  }`}
                  style={
                    drawerStep === "sections"
                      ? { background: THEME_GOLD, color: THEME_INK }
                      : undefined
                  }
                >
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black"
                    style={{
                      background: drawerStep === "sections" ? "rgba(31, 28, 25, 0.4)" : "rgba(0,0,0,0.08)",
                      color: drawerStep === "sections" ? THEME_INK : "inherit",
                    }}
                  >
                    2
                  </span>
                  Section
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div ref={bodyRef} className="overflow-y-auto flex-1">
              <AnimatePresence mode="wait">
                {drawerStep === "topics" ? (
                  <motion.div
                    key="topics-step"
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -16 }}
                    transition={{ duration: 0.2 }}
                    className="p-4"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      {FAQ_INDUSTRIES.map((ind, idx) => {
                        const IndIcon = I(ind.icon);
                        const isActive = ind.id === activeIndustryId;
                        const count = topicFaqCount(ind.id);
                        return (
                          <motion.button
                            key={ind.id}
                            type="button"
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: idx * 0.04, duration: 0.3 }}
                            onClick={() => handleTopicPick(ind.id)}
                            className={`relative group flex flex-col items-start gap-2 p-4 rounded-2xl text-left border-2 transition-all duration-200 overflow-hidden ${
                              isActive
                                ? "border-transparent shadow-lg"
                                : "border-black/[0.06] bg-white hover:border-[#FFD221]/45 hover:shadow-md"
                            }`}
                            style={isActive ? { background: THEME_GOLD, borderColor: "rgba(0,0,0,0.08)", boxShadow: shadowGoldTile } : {}}
                          >
                            {!isActive && (
                              <div
                                className="absolute inset-0 opacity-0 transition-opacity duration-300 pointer-events-none group-hover:opacity-100"
                                style={{ background: `${THEME_GOLD}0d` }}
                              />
                            )}
                            <div
                              className="relative flex h-10 w-10 items-center justify-center rounded-xl ring-1 ring-black/5"
                              style={{
                                background: isActive ? "rgba(0,0,0,0.12)" : `${THEME_GOLD}1f`,
                              }}
                            >
                              <IndIcon size={18} style={{ color: THEME_INK }} />
                            </div>
                            <div className="relative w-full min-w-0">
                              <p
                                className="line-clamp-2 text-xs font-bold leading-snug text-[#000000]"
                              >
                                {ind.label}
                              </p>
                              <p
                                className={`mt-1 text-[10px] font-semibold ${isActive ? "text-black/55" : "text-neutral-500"}`}
                              >
                                {count > 0 ? `${count} FAQs` : "Coming soon"}
                              </p>
                            </div>
                            {isActive && (
                              <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: `${THEME_GOLD}40` }}>
                                <Check size={11} strokeWidth={3} style={{ color: THEME_INK }} />
                              </div>
                            )}
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="sections-step"
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 16 }}
                    transition={{ duration: 0.2 }}
                    className="p-4"
                  >
                    {/* Back to topics */}
                    <button
                      type="button"
                      onClick={() => setDrawerStep("topics")}
                      className="flex gap-2 items-center mb-4 text-sm font-semibold text-gray-500 transition-colors hover:text-gray-800"
                    >
                      <ChevronLeft size={16} />
                      All topics
                    </button>

                    {/* Active topic card */}
                    <div
                      className="flex items-center gap-3 px-4 py-3.5 rounded-2xl mb-5"
                      style={{ background: `${THEME_GOLD}14`, border: `1.5px solid ${THEME_GOLD}40` }}
                    >
                      {(() => {
                        const IndIcon = I(activeIndustry.icon);
                        return (
                          <div
                            className="flex justify-center items-center w-9 h-9 rounded-xl shrink-0 ring-1 ring-black/5"
                            style={{ background: `${THEME_GOLD}35` }}
                          >
                            <IndIcon size={17} style={{ color: THEME_INK }} />
                          </div>
                        );
                      })()}
                      <div className="min-w-0">
                        <p className="text-xs font-black leading-snug text-[#000000]">{activeIndustry.label}</p>
                        <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                          {activeIndustryItems.length} section{activeIndustryItems.length !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>

                    {/* All categories button */}
                    <button
                      type="button"
                      onClick={() => { onCatChange(null); onClose(); }}
                      className={`mb-1.5 flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                        activeCatId === null
                          ? "text-black shadow-sm"
                          : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                      }`}
                      style={activeCatId === null ? { background: THEME_GOLD, color: THEME_INK, boxShadow: shadowGoldTile } : {}}
                    >
                      <span className="flex gap-2 items-center">
                        <BookOpen size={14} style={{ color: activeCatId === null ? THEME_INK : undefined }} />
                        All questions
                      </span>
                      <span
                        className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${
                          activeCatId === null ? "bg-black/10 text-black" : "bg-gray-200 text-gray-500"
                        }`}
                      >
                        {activeIndustryItems.reduce((acc, item) => {
                          const key = `${activeIndustryId}::${String(item.id)}`;
                          return acc + (ALL_FAQS_BY_ITEM_KEY.get(key)?.length ?? 0);
                        }, 0)}
                      </span>
                    </button>

                    {/* Category list */}
                    <div className="space-y-1">
                      {activeIndustryItems.map((item, idx) => {
                        const CatIcon = I(item.lucideIcon);
                        const isActive = String(activeCatId) === String(item.id);
                        const count = sectionFaqCount(item.id);
                        return (
                          <motion.button
                            key={item.id}
                            type="button"
                            initial={{ opacity: 0, x: 12 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.025, duration: 0.25 }}
                            onClick={() => { onCatChange(item.id); onClose(); }}
                            className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                              isActive
                                ? "text-black shadow-sm"
                                : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                            }`}
                            style={isActive ? { background: THEME_GOLD, boxShadow: shadowGoldTileSm } : {}}
                          >
                            <span className="flex gap-2 items-center min-w-0">
                              <CatIcon
                                size={14}
                                strokeWidth={2}
                                style={{ color: THEME_INK, flexShrink: 0 }}
                              />
                              <span className="truncate">{item.name}</span>
                            </span>
                            {count > 0 && (
                              <span
                                className={`ml-2 shrink-0 rounded-lg px-2 py-0.5 text-[10px] font-bold ${
                                  isActive ? "bg-black/10 text-black" : "bg-gray-200 text-gray-500"
                                }`}
                              >
                                {count}
                              </span>
                            )}
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Drawer footer */}
            <div className="px-4 pt-3 pb-6 border-t border-gray-100 shrink-0 bg-gray-50/80">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 text-sm font-bold rounded-xl transition-all shadow-lg"
                  style={{ background: THEME_GOLD, color: THEME_INK, boxShadow: "0 10px 28px -8px rgba(255,210,33,0.55)" }}
                >
                  Apply & Browse
                </button>
                <button
                  type="button"
                  onClick={() => { onIndustryChange(DEFAULT_TOPIC_ID); onCatChange(null); onClose(); }}
                  className="px-4 py-3 text-sm font-semibold text-gray-600 bg-gray-200 rounded-xl transition-colors hover:bg-gray-300"
                >
                  Reset
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

/* ─── Main page ─────────────────────────────────── */
const FAQsPage = () => {
  const [activeIndustryId, setActiveIndustryId] = useState(DEFAULT_TOPIC_ID);
  const [activeCatId, setActiveCatId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarScrollPct, setSidebarScrollPct] = useState(0);
  const [sidebarScrolling, setSidebarScrolling] = useState(false);
  const searchRef = useRef(null);
  const scrollIdleTimerRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const onScroll = () => {
      setSidebarScrolling(true);
      if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
      scrollIdleTimerRef.current = setTimeout(() => setSidebarScrolling(false), 180);

      const el = document.documentElement;
      const scrollable = el.scrollHeight - el.clientHeight;
      const pct = scrollable > 0 ? Math.round((100 * el.scrollTop) / scrollable) : 0;
      setSidebarScrollPct(pct);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 280);
    return () => clearTimeout(t);
  }, [search]);

  const activeIndustry = FAQ_INDUSTRIES.find((i) => i.id === activeIndustryId) ?? FAQ_INDUSTRIES[0];
  const activeIndustryItems = getFaqItemsByIndustry(activeIndustry.id);

  const handleIndustryChange = useCallback((id) => {
    setActiveIndustryId(id);
    setActiveCatId(null);
    setOpenId(null);
    setSearch("");
  }, []);

  const handleCatChange = useCallback((id) => {
    setActiveCatId(id);
    setOpenId(null);
  }, []);

  useEffect(() => {
    if (!activeCatId) return;
    const exists = activeIndustryItems.some((c) => String(c.id) === String(activeCatId));
    if (!exists) setActiveCatId(null);
  }, [activeIndustryItems, activeCatId]);

  const visibleCats = useMemo(() => {
    const items = activeCatId
      ? activeIndustryItems.filter((i) => String(i.id) === String(activeCatId))
      : activeIndustryItems;
    return items.map((item) => ({
      id: item.id,
      label: item.name,
      icon: item.lucideIcon,
      faqs: ALL_FAQS_BY_ITEM_KEY.get(`${activeIndustry.id}::${String(item.id)}`) ?? [],
    }));
  }, [activeCatId, activeIndustry.id, activeIndustryItems]);

  const searchResults = useMemo(() => {
    if (!debouncedSearch) return [];
    const q = debouncedSearch.toLowerCase();
    return SEARCHABLE_FAQS_FLAT.filter(
      (faq) =>
        faq.q.toLowerCase().includes(q) ||
        faq.a.toLowerCase().includes(q) ||
        faq.categoryLabel.toLowerCase().includes(q) ||
        faq.industryLabel.toLowerCase().includes(q)
    );
  }, [debouncedSearch]);

  const isSearching = debouncedSearch.length > 0;
  const color = THEME_GOLD;

  const activeIndustryFaqCount = activeIndustryItems.reduce((acc, item) => {
    return acc + (ALL_FAQS_BY_ITEM_KEY.get(`${activeIndustry.id}::${String(item.id)}`)?.length ?? 0);
  }, 0);

  const selectedItem = activeCatId
    ? activeIndustryItems.find((i) => String(i.id) === String(activeCatId))
    : null;

  const showTopicCategoryNav = activeIndustryItems.length > 1;

  const IndIcon = I(activeIndustry.icon);

  return (
    <div className="min-h-screen bg-[#f7f4ec]">
      {/* ─── Hero — split layout, glass stats, curved bottom ───────────────── */}
      <section className="relative overflow-hidden pt-28 pb-16 sm:pb-20 md:pb-24">
        <div className="absolute inset-0 bg-white" aria-hidden />
        <div className="absolute inset-0" aria-hidden style={{ background: gradLightHero }} />

        <motion.div
          className="pointer-events-none absolute -top-28 -right-12 h-[min(100vw,420px)] w-[min(100vw,420px)] rounded-full blur-[90px] opacity-90"
          style={{ background: `radial-gradient(circle, ${THEME_GOLD}45 0%, transparent 62%)` }}
          animate={{ scale: [1, 1.07, 1], opacity: [0.72, 0.88, 0.72] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden
        />
        <motion.div
          className="pointer-events-none absolute -bottom-36 -left-24 h-[380px] w-[380px] rounded-full blur-[75px] opacity-70"
          style={{ background: "radial-gradient(circle, rgba(120, 110, 98, 0.42) 0%, transparent 65%)" }}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
          aria-hidden
        />

        <div
          className="pointer-events-none absolute inset-x-0 top-[22%] h-px max-w-2xl mx-auto opacity-[0.35] sm:opacity-50"
          style={{ background: `linear-gradient(90deg, transparent, ${THEME_GOLD}, transparent)` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.11]"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
          aria-hidden
        />

        <div className="relative z-10 mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-12 lg:gap-14 lg:items-center">
            <div className="text-center lg:col-span-7 lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-6 flex justify-center lg:justify-start"
              >
                <div
                  className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 shadow-[0_0_28px_-6px_rgba(255,210,33,0.5)]"
                  style={{ borderColor: `${THEME_GOLD}50`, background: `${THEME_GOLD}12` }}
                >
                  <BookOpen size={12} style={{ color: THEME_GOLD }} />
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: THEME_GOLD }}>
                    InfoBoxx FAQs
                  </span>
                </div>
              </motion.div>

              <div className="flex flex-col justify-center gap-5 lg:flex-row lg:items-stretch lg:gap-8 lg:justify-start">
                <motion.div
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="hidden origin-top rounded-full lg:block w-1.5 shrink-0 self-stretch min-h-[7.5rem]"
                  style={{ background: `linear-gradient(180deg, ${THEME_GOLD} 0%, ${THEME_GOLD}55 45%, transparent 100%)` }}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <motion.h1
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: 0.05 }}
                    className="font-black tracking-tight text-gray-950"
                  >
                    <span className="block text-[1.65rem] leading-[1.15] sm:text-4xl lg:text-[2.75rem]">
                      Answers that move
                    </span>
                    <span
                      className="mt-1 block text-[1.85rem] leading-none sm:text-[2.75rem] lg:text-[3.25rem]"
                      style={{ color: THEME_GOLD }}
                    >
                      you forward
                    </span>
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-gray-600 sm:text-base lg:mx-0"
                  >
                    {TOTAL_FAQS}+ trusted answers across {FAQ_INDUSTRIES.length} InfoBoxx topics, built for people who need clarity before they choose a provider.
                    <br className="hidden sm:block" />
                    Search once, filter by topic, then continue into the right content journey.
                  </motion.p>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="relative mt-9 max-w-lg mx-auto lg:mx-0 rounded-2xl p-[2px]"
                style={{
                  background: `linear-gradient(125deg, ${THEME_GOLD}70, rgba(255,255,255,0.22), ${THEME_GOLD}40)`,
                }}
              >
                <div className="rounded-[14px] bg-white p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)]">
                  <div className="relative">
                    <Search size={17} className="absolute left-4 top-1/2 text-neutral-400 -translate-y-1/2 pointer-events-none" />
                    <input
                      ref={searchRef}
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search all questions…"
                      className="w-full rounded-xl border border-black/10 bg-white py-3.5 pl-11 pr-12 text-sm text-[#000000] placeholder-neutral-400 transition-all focus:outline-none focus:ring-2 focus:ring-[#FFD221] ring-offset-2 ring-offset-white"
                      style={{ "--tw-ring-color": THEME_GOLD }}
                    />
                    {search ? (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        className="flex absolute right-3 top-1/2 justify-center items-center w-7 h-7 text-neutral-600 rounded-full transition-all -translate-y-1/2 hover:bg-neutral-100"
                        style={{ background: `${THEME_GOLD}22` }}
                      >
                        <X size={13} />
                      </button>
                    ) : (
                      <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        <kbd className="text-[10px] text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded font-mono border border-black/5">
                          ⌘K
                        </kbd>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.22 }}
                className="grid grid-cols-3 gap-2 gap-y-2.5 mt-7 w-full max-w-2xl mx-auto lg:mx-0"
              >
                {FAQ_INDUSTRIES.map((ind) => {
                  const IndIcon2 = I(ind.icon);
                  const isActive = ind.id === activeIndustryId && !isSearching;
                  return (
                    <button
                      key={ind.id}
                      type="button"
                      onClick={() => handleIndustryChange(ind.id)}
                      className="inline-flex w-full min-w-0 items-center justify-center gap-1.5 px-2 py-2 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-[11px] font-semibold transition-all border text-center leading-tight"
                      style={
                        isActive
                          ? {
                              background: THEME_GOLD,
                              color: THEME_INK,
                              borderColor: "transparent",
                              boxShadow: `0 8px 24px -6px ${THEME_GOLD}99`,
                            }
                          : {
                              background: THEME_CREAM,
                              color: "rgba(0,0,0,0.75)",
                              borderColor: "rgba(0,0,0,0.08)",
                            }
                      }
                    >
                      <IndIcon2 size={11} strokeWidth={isActive ? 2.5 : 2} className="shrink-0" />
                      <span className="min-w-0 line-clamp-2">{ind.label}</span>
                    </button>
                  );
                })}
              </motion.div>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative mx-auto w-full max-w-sm lg:max-w-none"
              >
                <motion.div
                  className="pointer-events-none absolute -right-4 top-10 -z-10 hidden h-44 w-44 rounded-full border-2 border-dashed opacity-[0.22] sm:block"
                  style={{ borderColor: THEME_GOLD }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 42, repeat: Infinity, ease: "linear" }}
                  aria-hidden
                />
                <div className="rounded-[1.65rem] border border-black/10 bg-white p-6 shadow-[0_28px_64px_-24px_rgba(0,0,0,0.12)] sm:p-7">
                  <div className="mb-5 flex items-center gap-2.5">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-xl border border-black/10"
                      style={{ background: `${THEME_GOLD}20` }}
                    >
                      <Sparkles size={17} style={{ color: THEME_GOLD }} strokeWidth={2} />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-gray-500">
                      At a glance
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-black/[0.08] bg-[#f7f4ec] px-4 py-4">
                      <p className="text-3xl font-black tabular-nums leading-none text-gray-950">{TOTAL_FAQS}+</p>
                      <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                        Answers
                      </p>
                    </div>
                    <div
                      className="rounded-2xl border px-4 py-4"
                      style={{
                        background: `${THEME_GOLD}14`,
                        borderColor: `${THEME_GOLD}40`,
                      }}
                    >
                      <p
                        className="text-3xl font-black leading-none tabular-nums"
                        style={{ color: THEME_GOLD }}
                      >
                        {FAQ_INDUSTRIES.length}
                      </p>
                      <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-600">
                        Industries
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 space-y-2 border-t border-black/10 pt-5">
                    {FEATURED_HELP_CARDS.map(({ title, text, icon: HelpIcon }) => (
                      <div key={title} className="flex gap-3 rounded-2xl border border-black/[0.06] bg-[#f7f4ec] p-3">
                        <span
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                          style={{ background: `${THEME_GOLD}24`, color: THEME_INK }}
                        >
                          <HelpIcon size={16} />
                        </span>
                        <span>
                          <span className="block text-sm font-black text-gray-950">{title}</span>
                          <span className="mt-1 block text-xs leading-relaxed text-gray-500">{text}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Content ──────────────────────────────── */}
      <div className="relative px-4 py-10 mx-auto max-w-8xl sm:px-6 lg:px-8 lg:py-14">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#FFD221]/[0.09] to-transparent"
          aria-hidden
        />

        {!isSearching && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.45 }}
            className="relative mb-10 overflow-hidden rounded-[2rem] border border-black/[0.08] bg-black text-white shadow-[0_24px_70px_-35px_rgba(0,0,0,0.75)]"
          >
            <div
              className="absolute inset-0 opacity-25"
              style={{
                background: `radial-gradient(circle at 20% 10%, ${THEME_GOLD} 0%, transparent 28%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.35) 0%, transparent 24%)`,
              }}
              aria-hidden
            />
            <div className="relative grid gap-6 p-6 sm:p-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#FFD221]">
                  Help center map
                </p>
                <h2 className="mt-3 text-2xl font-black leading-tight sm:text-4xl">
                  The fastest path is search, topic, section, answer.
                </h2>
                <p className="mt-4 max-w-xl text-sm leading-7 text-white/60">
                  The FAQ page now behaves like a guided support desk: quick topic
                  cards for scanning, filters for precision, and contact routes
                  when a question needs a human.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {FEATURED_HELP_CARDS.map(({ title, text, icon: HelpIcon }) => (
                  <button
                    key={title}
                    type="button"
                    onClick={() => {
                      if (title === "Start with a topic") setDrawerOpen(true);
                      if (title === "Search across InfoBoxx") searchRef.current?.focus();
                      if (title === "Still unsure?") window.location.assign("/contact");
                    }}
                    className="rounded-3xl border border-white/10 bg-white/10 p-4 text-left backdrop-blur transition hover:bg-white/15"
                  >
                    <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFD221] text-black">
                      <HelpIcon size={18} />
                    </span>
                    <span className="block text-sm font-black text-white">{title}</span>
                    <span className="mt-2 block text-xs leading-relaxed text-white/55">{text}</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {isSearching ? (
          /* ─── Search results ─────────────────── */
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: THEME_GOLD }}>Search results</p>
                <h2 className="text-2xl font-black text-[#000000]">
                  {searchResults.length > 0
                    ? <><span style={{ color: THEME_GOLD }}>{searchResults.length}</span> result{searchResults.length !== 1 ? "s" : ""} for "{debouncedSearch}"</>
                    : `No results for "${debouncedSearch}"`
                  }
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setSearch("")}
                className="flex items-center gap-1.5 text-sm font-semibold text-[#000000] hover:bg-[#FFD221]/15 px-4 py-2 rounded-xl border border-black/[0.08] transition-all shadow-sm"
              >
                <X size={14} />
                Clear
              </button>
            </div>
            <SearchResults results={searchResults} openId={openId} setOpenId={setOpenId} />
          </div>
        ) : (
          <>
            {/* ─── Topic cards (six in one row; horizontal scroll on narrow screens) ────── */}
            <div className="mb-2">
              <div className="flex justify-between items-center mb-3">
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-gray-400">
                  Browse by topic
                </p>
                <motion.button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex gap-2 items-center rounded-xl bg-gray-950 px-4 py-2 text-xs font-bold text-white shadow-md transition-all hover:shadow-lg"
                  style={{ boxShadow: shadowGoldSm }}
                >
                  <SlidersHorizontal size={13} style={{ color: THEME_GOLD }} />
                  Advanced filter
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black"
                    style={{ background: THEME_GOLD, color: THEME_INK }}
                  >
                    {activeCatId ? "2" : "1"}
                  </span>
                </motion.button>
              </div>

              <div className="flex flex-nowrap gap-2 w-full max-w-full overflow-x-auto overflow-y-visible pb-1 [scrollbar-width:thin] md:overflow-visible md:pb-0 md:grid md:grid-cols-6 md:gap-2">
                {FAQ_INDUSTRIES.map((ind, idx) => {
                  const IndBtn = I(ind.icon);
                  const isActive = ind.id === activeIndustryId;
                  return (
                    <motion.button
                      key={ind.id}
                      type="button"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.035, duration: 0.3 }}
                      onClick={() => handleIndustryChange(ind.id)}
                      className={`relative group flex flex-col items-center gap-1.5 px-2 py-3 rounded-2xl border-2 text-center transition-all duration-200 overflow-hidden flex-shrink-0 w-[min(100%,8.75rem)] min-w-[6.75rem] md:w-auto md:min-w-0 min-h-[5.5rem] ${
                        isActive
                          ? "border-transparent shadow-lg"
                          : "border-black/[0.06] bg-white hover:border-[#FFD221]/50 hover:shadow-md"
                      }`}
                      style={isActive ? { background: THEME_GOLD, borderColor: "rgba(0,0,0,0.08)", boxShadow: shadowGoldTile } : {}}
                    >
                      {!isActive && (
                        <div
                          className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 pointer-events-none group-hover:opacity-100"
                          style={{ background: `${THEME_GOLD}0d` }}
                        />
                      )}
                      <div
                        className="flex relative justify-center items-center w-8 h-8 rounded-xl shrink-0 ring-1 ring-black/5"
                        style={{ background: isActive ? "rgba(0,0,0,0.12)" : `${THEME_GOLD}1f` }}
                      >
                        <IndBtn size={15} style={{ color: THEME_INK }} />
                      </div>
                      <p
                        className="relative line-clamp-2 text-[11px] font-bold leading-tight text-[#000000]"
                      >
                        {ind.label}
                      </p>
                      {isActive && (
                        <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: `${THEME_GOLD}35` }}>
                          <Check size={9} strokeWidth={3} style={{ color: THEME_INK }} />
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* ─── Layout: Category sidebar + FAQ blocks ─────────── */}
            <div className="grid lg:grid-cols-[300px_1fr] gap-6 lg:gap-8 pt-5">
              {/* Sidebar */}
              <aside className="hidden lg:block">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndustryId}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -14 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    className="sticky top-24 space-y-4"
                  >
                    <div className="overflow-hidden bg-white rounded-2xl border border-black/[0.07] shadow-[0_8px_30px_-12px_rgba(0,0,0,0.08)]">
                      <div
                        className="px-5 py-4 border-b border-black/[0.06]"
                        style={{ background: `linear-gradient(135deg, ${THEME_GOLD}14 0%, transparent 70%)` }}
                      >
                        <p className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: THEME_INK }}>
                          {showTopicCategoryNav ? "In this topic" : "Overview"}
                        </p>
                        <p className="mt-1 text-sm font-black text-[#000000]">{activeIndustry.label}</p>
                        <p className="mt-1 text-xs text-gray-400">
                          {showTopicCategoryNav
                            ? `${activeIndustryItems.length} sections · ${activeIndustryFaqCount} questions`
                            : `${activeIndustryFaqCount} questions · scroll the list to read answers`}
                        </p>
                      </div>

                      <div className="p-3">
                        {showTopicCategoryNav ? (
                          <>
                            <button
                              type="button"
                              onClick={() => handleCatChange(null)}
                              className={`w-full flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl text-sm font-bold transition-all ${
                                activeCatId == null ? "text-black shadow-md" : "text-[#000000] bg-neutral-50 hover:bg-[#FFD221]/[0.12]"
                              }`}
                              style={activeCatId == null ? { background: THEME_GOLD, boxShadow: shadowGoldTile } : {}}
                            >
                              <span className="flex gap-2 items-center min-w-0">
                                <BookOpen size={14} style={{ color: THEME_INK }} />
                                <span className="truncate">All sections</span>
                              </span>
                              <span
                                className={`text-[10px] font-black px-2 py-0.5 rounded-lg shrink-0 ${
                                  activeCatId == null ? "bg-black/10 text-black" : "bg-gray-200 text-gray-500"
                                }`}
                              >
                                {activeIndustryFaqCount}
                              </span>
                            </button>

                            <div className="mt-2 space-y-1 max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
                              {activeIndustryItems.map((item) => {
                                const CatIcon = I(item.lucideIcon);
                                const isActive = String(activeCatId) === String(item.id);
                                const count = ALL_FAQS_BY_ITEM_KEY.get(`${activeIndustry.id}::${String(item.id)}`)?.length ?? 0;
                                return (
                                  <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => handleCatChange(item.id)}
                                    className={`w-full flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                                      isActive ? "text-black shadow-md" : "text-[#000000] hover:bg-[#FFD221]/[0.12]"
                                    }`}
                                    style={isActive ? { background: THEME_GOLD, boxShadow: shadowGoldTileSm } : {}}
                                  >
                                    <span className="flex gap-2 items-center min-w-0">
                                      <CatIcon
                                        size={14}
                                        strokeWidth={2}
                                        style={{ color: THEME_INK, flexShrink: 0 }}
                                      />
                                      <span className="truncate">{item.name}</span>
                                    </span>
                                    {count > 0 && (
                                      <span
                                        className={`text-[10px] font-black px-2 py-0.5 rounded-lg shrink-0 ${
                                          isActive ? "bg-black/10 text-black" : "bg-gray-200 text-gray-500"
                                        }`}
                                      >
                                        {count}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        ) : (
                          <p className="px-1 text-xs leading-relaxed text-neutral-500">
                            Answers open on the right. Tap a question to expand — only one section stays open for easier reading on long lists.
                          </p>
                        )}
                      </div>
                    </div>

                    <div
                      className="overflow-hidden rounded-2xl border border-black/[0.06] bg-gradient-to-b from-white to-neutral-50/90 px-4 py-4 shadow-[0_8px_28px_-14px_rgba(0,0,0,0.1)]"
                      aria-live="polite"
                    >
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-neutral-400">
                          Page progress
                        </p>
                        <AnimatePresence>
                          {sidebarScrolling && (
                            <motion.span
                              key="scroll-hint"
                              initial={{ opacity: 0, x: 6 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 4 }}
                              transition={{ duration: 0.2 }}
                              className="text-[10px] font-bold text-[#000000]/45"
                            >
                              Scrolling…
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <SidebarScrollDoughnut scrollPct={sidebarScrollPct} isScrolling={sidebarScrolling} />
                        <p className="mt-1 max-w-[14rem] text-center text-[11px] leading-snug text-neutral-500">
                          See how far you’ve moved through this page. The ring gives a quick spin while you scroll so you know it’s keeping up.
                        </p>
                      </div>
                    </div>

                    {/* Keep drawer for power users */}
                    <button
                      type="button"
                      onClick={() => setDrawerOpen(true)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gray-950 px-4 py-3 text-sm font-bold text-white shadow-md transition-all hover:shadow-lg"
                      style={{ boxShadow: shadowGold }}
                    >
                      <SlidersHorizontal size={15} style={{ color: THEME_GOLD }} />
                      Open Filter Drawer
                    </button>
                  </motion.div>
                </AnimatePresence>
              </aside>

              {/* Main: FAQ blocks */}
              <div>
                {/* Mobile helper row (drawer) */}
                <div className="flex justify-between items-center mb-4 lg:hidden">
                  <div className="flex gap-2 items-center">
                    <div className="h-4 w-1 rounded-full" style={{ background: `linear-gradient(180deg, ${THEME_GOLD}, ${THEME_INK})` }} />
                    <p className="text-[11px] font-bold text-neutral-600">
                      {activeIndustry.label}{selectedItem ? ` · ${selectedItem.name}` : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-2 text-xs font-bold text-white shadow-md"
                  >
                    <SlidersHorizontal size={13} style={{ color: THEME_GOLD }} />
                    Filter
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${activeIndustryId}-${activeCatId}`}
                    variants={stagger}
                    initial="hidden"
                    animate="visible"
                    exit={{ opacity: 0, y: -10, transition: { duration: 0.15 } }}
                    className="space-y-4"
                  >
                    {visibleCats.length === 0 ? (
                      <motion.div
                        variants={fadeUp}
                        className="flex flex-col justify-center items-center py-20 text-center"
                      >
                        <div
                          className="flex justify-center items-center mb-5 w-20 h-20 rounded-3xl ring-1 ring-black/5"
                          style={{ background: `${THEME_GOLD}18` }}
                        >
                          <IndIcon size={32} style={{ color: THEME_INK, opacity: 0.45 }} />
                        </div>
                        <p className="mb-2 text-lg font-bold text-[#000000]">No FAQs here yet</p>
                        <p className="max-w-xs text-sm text-gray-400">More questions for this topic are coming soon.</p>
                      </motion.div>
                    ) : (
                      visibleCats.map((cat) => (
                        <CategoryBlock
                          key={cat.id}
                          cat={cat}
                          color={color}
                          openId={openId}
                          setOpenId={setOpenId}
                        />
                      ))
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* ─── Bottom CTA ─────────────────────── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mt-14 overflow-hidden rounded-3xl border border-[#FFD221]/30 ring-1 ring-black/5"
              style={{ background: gradLightCta, boxShadow: shadowGold }}
            >
              <div className="relative px-7 py-10 sm:px-10 sm:py-12">
                <div
                  className="pointer-events-none absolute inset-0 opacity-[0.35]"
                  style={{
                    backgroundImage: "radial-gradient(circle at 1px 1px,rgba(0,0,0,.06) 1px,transparent 0)",
                    backgroundSize: "24px 24px",
                  }}
                />
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{ background: `radial-gradient(ellipse at 75% 50%, ${THEME_GOLD}30, transparent 55%)` }}
                />
                <div className="relative flex flex-col gap-7 sm:flex-row sm:items-center">
                  <div className="flex-1">
                    <p className="mb-3 text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: "#9a7b00" }}>
                      Still have questions?
                    </p>
                    <h3 className="mb-2 text-2xl font-black leading-snug text-gray-950">
                      Our team is here to help
                    </h3>
                    <p className="max-w-sm text-sm leading-relaxed text-gray-600">
                      Can&apos;t find what you&apos;re looking for? Our support team responds within one business day.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3 sm:flex-col">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-black text-black shadow-lg transition-all hover:opacity-95"
                      style={{ background: THEME_GOLD, boxShadow: "0 14px 36px -8px rgba(255,210,33,0.55)" }}
                    >
                      <Mail size={15} />
                      Contact Us
                    </Link>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-6 py-3 text-sm font-semibold text-gray-800 transition-colors hover:bg-[#f7f4ec]"
                    >
                      <ChevronLeft size={15} />
                      Go back
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </div>

      {/* ─── Floating filter FAB (mobile) ────────── */}
      {!isSearching && (
        <motion.button
          type="button"
          onClick={() => setDrawerOpen(true)}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.6, type: "spring", stiffness: 280, damping: 22 }}
          whileHover={{ scale: 1.07 }}
          whileTap={{ scale: 0.94 }}
          className="fixed bottom-6 right-5 z-30 flex items-center gap-2.5 rounded-2xl bg-[#FFD221] px-5 py-3.5 text-sm font-black text-black shadow-2xl sm:hidden"
          style={{ boxShadow: shadowGoldFab }}
        >
          <SlidersHorizontal size={16} style={{ color: THEME_INK }} />
          Filter
          <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black" style={{ background: THEME_GOLD, color: THEME_INK }}>
            {activeCatId ? "2" : "1"}
          </span>
        </motion.button>
      )}

      {/* ─── Filter Drawer ────────────────────────── */}
      <FilterDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeIndustryId={activeIndustryId}
        activeCatId={activeCatId}
        onIndustryChange={handleIndustryChange}
        onCatChange={handleCatChange}
      />

      <Footer />
    </div>
  );
};

export default FAQsPage;
